import SendRounded from '@mui/icons-material/SendRounded';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Conversation from 'components/common/Conversation';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useCreateDemochatClientMutation,
  useCreateDemochatMessageMutation,
  useGetDemoClientMessagesQuery,
  useGetWidgetConfigQuery,
} from 'services/api/demochat';
import { NAVY, RADIUS_COIN, SURFACE_CHAT, SURFACE_CONTAINER_LOWEST, SURFACE_TINT, YELLOW } from 'styles/tokens';
import { clearClientPhoneIdentifier, getClientPhoneIdentifier, setClientPhoneIdentifier } from 'utils/chatStorage';
import getReCaptchaToken from 'utils/getReCaptchaToken';
import { subscribeChannel } from 'utils/pusherClient';

import ChatIntro from './ChatIntro';
import ChatWrapper from './ChatWrapper';
import { buildGreetingMessage, buildPendingMessage } from './widgetMessages';

const DEFAULT_GREETING = '¡Hola! Estoy aquí para ayudarte. ¿Qué necesitas?';
const HANDOFF_NOTICE = 'Tu conversación siguió por WhatsApp. Escribe aquí para empezar una nueva.';

const isHandoffError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: number }).status === 409;

const WidgetChat = () => {
  const { companyIdentifier } = useParams<{ companyIdentifier: string }>();
  const [clientPhoneIdentifier, setClientPhone] = useState<string | null>(() =>
    getClientPhoneIdentifier(companyIdentifier)
  );
  const [messageToSend, setMessageToSend] = useState('');
  const [pendingMessageText, setPendingMessageText] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [showHandoffNotice, setShowHandoffNotice] = useState(false);
  const [refetchFlag, setRefetchFlag] = useState<number>(1);

  const [createDemochatClient] = useCreateDemochatClientMutation();
  const [createDemochatMessage] = useCreateDemochatMessageMutation();

  const {
    data: messages,
    refetch,
    error: messagesError,
  } = useGetDemoClientMessagesQuery(
    { clientPhoneIdentifier: clientPhoneIdentifier ?? '' },
    { skip: !clientPhoneIdentifier }
  );
  const { data: widgetConfig } = useGetWidgetConfigQuery(
    { companyIdentifier: companyIdentifier ?? '' },
    { skip: !companyIdentifier }
  );

  const theme = widgetConfig?.theme;
  const copy = widgetConfig?.copy;
  const primaryColor = theme?.primaryColor || YELLOW;
  const onPrimaryColor = theme?.onPrimaryColor || NAVY;
  const chatBackground = theme?.chatBackground || SURFACE_CHAT;
  const inputPlaceholder = copy?.inputPlaceholder || 'Escribe un mensaje…';
  const greeting = copy?.greeting || copy?.welcomeBody || DEFAULT_GREETING;
  const suggestedQuestions = copy?.suggestedQuestions ?? [];

  // A handed-off conversation lives on WhatsApp now, so the widget drops its identifier and the
  // next message opens a fresh one.
  const resetAfterHandoff = () => {
    clearClientPhoneIdentifier(companyIdentifier);
    setClientPhone(null);
    setPendingMessageText(null);
    setShowHandoffNotice(true);
  };

  useEffect(() => {
    if (messagesError && isHandoffError(messagesError)) {
      resetAfterHandoff();
    }
  }, [messagesError]);

  useEffect(() => {
    if (!clientPhoneIdentifier) return;
    refetch().catch(() => {
      console.error('Error refetching messages');
    });
  }, [refetchFlag]);

  useEffect(() => {
    if (!clientPhoneIdentifier) return () => {};
    const channelName = `chat-${clientPhoneIdentifier}`;
    const { channel, unsubscribe } = subscribeChannel(channelName);
    const handler = () => {
      setRefetchFlag((prev) => prev + 1);
    };
    channel.bind('new-message', handler);
    return () => {
      channel.unbind('new-message', handler);
      unsubscribe();
    };
  }, [clientPhoneIdentifier]);

  useEffect(() => {
    if (!pendingMessageText) return;
    const isEchoed = messages?.some(
      (message) => message.role === 'user' && message.text?.trim() === pendingMessageText
    );
    if (isEchoed) setPendingMessageText(null);
  }, [messages, pendingMessageText]);

  const conversation = useMemo(() => {
    const pending = pendingMessageText ? [buildPendingMessage(pendingMessageText)] : [];
    return [buildGreetingMessage(greeting), ...(messages ?? []), ...pending];
  }, [greeting, messages, pendingMessageText]);

  // Returning visitors go straight to their conversation: waiting for the first fetch avoids
  // flashing the intro over an existing chat.
  const isLoadingExistingChat = Boolean(clientPhoneIdentifier) && messages === undefined;
  const hasStarted = isLoadingExistingChat || (messages?.length ?? 0) > 0 || Boolean(pendingMessageText);
  const lastMessageRole = conversation.at(-1)?.role;
  const isAssistantWriting = isSending || lastMessageRole === 'user';

  const startConversation = async (): Promise<string> => {
    if (clientPhoneIdentifier) return clientPhoneIdentifier;
    if (!companyIdentifier) throw new Error('Missing companyIdentifier');
    const params = new URLSearchParams(window.location.search);
    const recaptchaToken = await getReCaptchaToken();
    const { id } = await createDemochatClient({
      companyIdentifier,
      recaptchaToken,
      isFromWidget: true,
      source: params.get('source') ?? undefined,
      utmSource: params.get('utm_source') ?? undefined,
      utmMedium: params.get('utm_medium') ?? undefined,
      utmCampaign: params.get('utm_campaign') ?? undefined,
    }).unwrap();
    setClientPhoneIdentifier(companyIdentifier, id);
    setClientPhone(id);
    return id;
  };

  const sendMessage = (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || isSending || !companyIdentifier) return;
    setIsSending(true);
    setShowHandoffNotice(false);
    setMessageToSend('');
    setPendingMessageText(trimmedText);
    startConversation()
      .then((identifier) => createDemochatMessage({ clientPhoneIdentifier: identifier, message: trimmedText }).unwrap())
      .then(() => {
        setRefetchFlag((prev) => prev + 1);
      })
      .catch((error) => {
        if (isHandoffError(error)) {
          resetAfterHandoff();
          return;
        }
        console.error('Error sending widget chat message', error);
        setPendingMessageText(null);
        setMessageToSend(trimmedText);
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <ChatWrapper companyIdentifier={companyIdentifier}>
      {/* biome-ignore lint/complexity/noUselessFragments: ChatWrapper expects a single child */}
      <>
        <GlobalStyles
          styles={{
            // Hidden per product decision; a text disclosure below the input satisfies Google's ToS.
            '.grecaptcha-badge': { visibility: 'hidden' },
          }}
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: chatBackground,
          }}
        >
          {hasStarted ? (
            <Conversation
              conversation={conversation}
              isFilteredConversation
              isClientView
              outgoingBubbleColor={theme?.bubbleColor ?? undefined}
              outgoingBubbleTextColor={theme?.onBubbleColor ?? undefined}
              isAssistantWriting={isAssistantWriting}
              hideMessageMeta
              fullHeight
            />
          ) : (
            <ChatIntro
              greeting={greeting}
              suggestions={suggestedQuestions}
              onSelect={sendMessage}
              disabled={isSending}
            />
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            flexShrink: 0,
            px: 2,
            pt: 1.5,
            pb: 1,
            bgcolor: SURFACE_CONTAINER_LOWEST,
          }}
        >
          {showHandoffNotice && (
            <Typography
              sx={{ mb: 1, textAlign: 'center', fontSize: '0.75rem', color: 'rgba(21, 24, 58, 0.55)' }}
              role="status"
            >
              {HANDOFF_NOTICE}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              id="widget-message-input"
              placeholder={inputPlaceholder}
              fullWidth
              value={messageToSend}
              onChange={(e) => setMessageToSend(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(messageToSend);
                }
              }}
              sx={{
                backgroundColor: SURFACE_TINT,
                borderRadius: RADIUS_COIN,
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_COIN,
                  '& fieldset': { border: 'none' },
                },
                '& .MuiOutlinedInput-input': { py: 1.4, px: 2 },
              }}
            />
            <IconButton
              aria-label="Enviar mensaje"
              onClick={() => sendMessage(messageToSend)}
              disabled={!messageToSend.trim() || isSending}
              sx={{
                width: 46,
                height: 46,
                flexShrink: 0,
                backgroundColor: primaryColor,
                transition: 'transform 0.15s ease, opacity 0.15s ease',
                '&:hover': { backgroundColor: primaryColor, opacity: 0.9, transform: 'translateY(-1px)' },
                '&.Mui-disabled': { backgroundColor: SURFACE_TINT },
              }}
            >
              <SendRounded sx={{ fontSize: '1.35rem', color: onPrimaryColor }} />
            </IconButton>
          </Box>
          <Typography sx={{ mt: 0.75, textAlign: 'center', fontSize: '0.62rem', color: 'rgba(21, 24, 58, 0.45)' }}>
            Protegido por reCAPTCHA —{' '}
            <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener" color="inherit">
              Privacidad
            </Link>{' '}
            y{' '}
            <Link href="https://policies.google.com/terms" target="_blank" rel="noopener" color="inherit">
              Términos
            </Link>
          </Typography>
        </Box>
      </>
    </ChatWrapper>
  );
};

export default WidgetChat;
